import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
// types
import type { ComputedRef, Ref } from 'vue'
import type { StrapiSiteHeader } from '@ts-strapi/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'
// helpers
import { useElementSize } from '@vueuse/core'
import { resolveWpOriginUrl } from '@helpers/url'

interface SiteHeaderHookOptions {
  data: () => StrapiSiteHeader
  root: Ref<HTMLElement | null>
}

const NAV_CLOSE_DELAY_MS = 120

interface SiteHeaderHookReturn {
  refIsScrolled: Ref<boolean>
  refOpenedNav: Ref<number | null>
  refIsSheetOpen: Ref<boolean>
  refIsGlass: ComputedRef<boolean>
  refLoginHref: ComputedRef<string>
  refCtaHref: ComputedRef<string>
  refBannerHref: ComputedRef<string>
  resolveUrl: (url: string) => string
  toggleNav: (index: number) => void
  openNav: (index: number) => void
  closeNav: () => void
  scheduleCloseNav: () => void
  cancelCloseNav: () => void
  toggleSheet: () => void
  handleClickLogin: () => void
  handleClickCta: () => void
  handleClickBanner: () => void
  handleClickSeeAll: (label: string) => void
  handleNavFocusOut: (event: FocusEvent) => void
}

export const useSiteHeader = ({ data, root }: SiteHeaderHookOptions): SiteHeaderHookReturn => {
  const { public: { wpOriginUrl } } = useRuntimeConfig()
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'header' })
  const route = useRoute()

  const refIsScrolled = ref<boolean>(false)
  const refOpenedNav = ref<number | null>(null)
  const refIsSheetOpen = ref<boolean>(false)
  const { height: refHeaderHeight } = useElementSize(root, undefined, { box: 'border-box' })

  let closeNavTimer: ReturnType<typeof setTimeout> | null = null

  const refIsGlass = computed<boolean>(() => (
    refIsScrolled.value || refOpenedNav.value !== null || refIsSheetOpen.value
  ))

  const resolveUrl = (url: string): string => resolveWpOriginUrl(url, wpOriginUrl)

  const buttonHref = (button: { buttonText: string, buttonLink?: string | null, isDefaultUrl?: boolean }): string => {
    const base = button.isDefaultUrl ? refDefaultCtaUrl.value : (button.buttonLink ?? '#')
    return buildCtaHref(base, { text: button.buttonText })
  }

  const refLoginHref = computed<string>(() => buttonHref(data().login))
  const refCtaHref = computed<string>(() => buttonHref(data().cta))
  const refBannerHref = computed<string>(() => {
    const banner = data().banner
    return banner ? buttonHref(banner) : '#'
  })

  const closeAll = (): void => {
    refOpenedNav.value = null
    refIsSheetOpen.value = false
  }

  const cancelCloseNav = (): void => {
    if (closeNavTimer !== null) {
      clearTimeout(closeNavTimer)
      closeNavTimer = null
    }
  }

  const toggleNav = (index: number): void => {
    refOpenedNav.value = refOpenedNav.value === index ? null : index
  }

  const openNav = (index: number): void => {
    cancelCloseNav()
    refOpenedNav.value = index
  }

  const closeNav = (): void => {
    cancelCloseNav()
    refOpenedNav.value = null
  }

  const scheduleCloseNav = (): void => {
    cancelCloseNav()
    closeNavTimer = setTimeout(() => {
      refOpenedNav.value = null
      closeNavTimer = null
    }, NAV_CLOSE_DELAY_MS)
  }

  const toggleSheet = (): void => {
    refIsSheetOpen.value = !refIsSheetOpen.value
    refOpenedNav.value = null
  }

  const handleClickLogin = (): void => {
    trackSectionEvent('Button_click', { name: 'login', text: data().login.buttonText, url: refLoginHref.value })
  }

  const handleClickCta = (): void => {
    trackSectionEvent('Button_click', { name: 'book_lesson', text: data().cta.buttonText, url: refCtaHref.value })
  }

  const handleClickBanner = (): void => {
    trackSectionEvent('Banner_click', { name: 'banner', text: data().banner?.buttonText ?? '', url: refBannerHref.value })
  }

  const handleClickSeeAll = (label: string): void => {
    trackSectionEvent('Button_click', { name: 'see_all', text: label })
  }

  const handleScroll = (): void => {
    refIsScrolled.value = window.scrollY > 0
  }

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') closeAll()
  }

  const handlePointerDown = (event: PointerEvent): void => {
    if (refOpenedNav.value === null && !refIsSheetOpen.value) return
    if (root.value && !root.value.contains(event.target as Node)) closeAll()
  }

  const handleNavFocusOut = (event: FocusEvent): void => {
    if (refOpenedNav.value === null) return
    const nextTarget = event.relatedTarget as Node | null
    if (root.value && (nextTarget === null || !root.value.contains(nextTarget))) closeNav()
  }

  const applyHeaderHeight = (height: number): void => {
    if (height <= 0) return
    document.documentElement.style.setProperty('--site-header-height', `${Math.round(height)}px`)
  }

  onMounted(() => {
    handleScroll()
    applyHeaderHeight(refHeaderHeight.value)
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('pointerdown', handlePointerDown)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('pointerdown', handlePointerDown)
    document.body.style.overflow = ''
    document.documentElement.style.removeProperty('--site-header-height')
    cancelCloseNav()
  })

  watch(refIsSheetOpen, (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
  })

  watch(refHeaderHeight, applyHeaderHeight)

  watch(() => route.path, closeAll)

  return {
    refIsScrolled,
    refOpenedNav,
    refIsSheetOpen,
    refIsGlass,
    refLoginHref,
    refCtaHref,
    refBannerHref,
    resolveUrl,
    toggleNav,
    openNav,
    closeNav,
    scheduleCloseNav,
    cancelCloseNav,
    toggleSheet,
    handleClickLogin,
    handleClickCta,
    handleClickBanner,
    handleClickSeeAll,
    handleNavFocusOut,
  }
}
